import * as utils from './utils';

export type { ReduxDispatch } from './utils';

export interface IResponse<T = undefined> {
	onSuccess?: (res: T) => void;
	onFailure?: (err: unknown) => void;
}

export interface IQuery {
	key: string;
	value: string | number | boolean | null | undefined;
}

export interface IGetQuery<T = undefined> extends IResponse<T> {
	query?: IQuery[];
	paged?: boolean;
}

const handleQuery = (type: 'command' | 'core', paged?: boolean, ...query: IQuery[]) => {
	const pgType = type === 'command' ? 'get-all-paged' : 'paged';
	const pg = paged ? `/${pgType}` : '';
	if (!query?.length) return pg;
	const filteredQuery = query.filter((i) => i.key && i.value);
	const queries = filteredQuery.reduce((t, i, index) => {
		const andSign = index === filteredQuery.length - 1 ? '' : '&';
		t += `${i.key}=${i.value}${andSign}`;
		return t;
	}, '');
	if (!queries) return pg;
	return `${pg}?${queries}`;
};

export const getCoreQuery = (paged?: boolean, ...query: IQuery[]) => {
	const q = handleQuery('core', paged, ...query);
	return q || '';
};

export const getCommandQuery = (paged?: boolean, ...query: IQuery[]) => {
	const q = handleQuery('command', paged, ...query);
	return q || '';
};

export const getQuery = (type: 'command' | 'core', paged?: boolean, ...query: IQuery[]) => {
	const q = handleQuery(type, paged, ...query);
	return q || '';
};

export const createAPI =
	<L = undefined, T = unknown>({ actionType, url }: { actionType: utils.I_ACTION_TYPE; url: string }) =>
	({ data, onFailure, onSuccess }: IResponse<L> & { data?: T }) =>
		utils.httpPostMethod({
			apiData: {
				url,
				header: utils.header(),
				data,
			},
			actionType,
			onFailure,
			onSuccess,
		});

export const getAPI =
	<T = undefined>({
		actionType,
		url,
		customurl,
		type,
	}: {
		actionType: utils.I_ACTION_TYPE;
		url: string;
		customurl?: string;
		type?: 'command' | 'core';
	}) =>
	({ onFailure, onSuccess, query, paged }: IGetQuery<T>) =>
		utils.httpGetMethod({
			apiData: {
				url: !type ? url : `${url}${getQuery(type, paged, ...(query || []))}`,
				customurl,
				header: utils.header(),
			},
			actionType,
			onSuccess,
			onFailure,
		});

export const getFetchAPI =
	<T = undefined, L = unknown>({ actionType, url }: { actionType: utils.I_ACTION_TYPE; url: string }) =>
	({
		data,
		onFailure,
		onSuccess,
		params,
		pageNumber,
		pageSize,
	}: IResponse<T> & {
		data: L;
		params?: string;
		pageSize?: number;
		pageNumber?: number;
	}) =>
		utils.httpPostMethod({
			apiData: {
				url: `${url}${params ?? ''}`,
				header: utils.header(),
				data: {
					...data,
					pageSize,
					pageNumber,
				},
			},
			actionType,
			onFailure,
			onSuccess,
		});

export const getByIdAPI =
	<T = undefined>({ actionType, url }: { actionType: utils.I_ACTION_TYPE; url: string }) =>
	({ id, onFailure, onSuccess }: IResponse<T> & { id: string }) =>
		utils.httpGetMethod({
			apiData: {
				url: `${url}/${id}`,
				header: utils.header(),
			},
			actionType,
			onFailure,
			onSuccess,
		});

export const getByNameAPI =
	<T = undefined, L = string>({ actionType, url }: { actionType: utils.I_ACTION_TYPE; url: string }) =>
	({ name, onFailure, onSuccess }: IResponse<T> & { name: L }) =>
		utils.httpPostMethod({
			apiData: {
				url,
				header: utils.header(),
				data: { name },
			},
			actionType,
			onFailure,
			onSuccess,
		});

export const updateAPI =
	<L = undefined, T = unknown>({ actionType, url }: { actionType: utils.I_ACTION_TYPE; url: string }) =>
	({
		data,
		onFailure,
		onSuccess,
	}: IResponse<L> & {
		data: T;
	}) =>
		utils.httpPutMethod({
			apiData: {
				url,
				header: utils.header(),
				data,
			},
			actionType,
			onFailure,
			onSuccess,
		});

export const updateByIdAPI =
	<T = undefined, L = unknown>({ actionType, url }: { actionType: utils.I_ACTION_TYPE; url: string }) =>
	({
		onFailure,
		onSuccess,
		id,
		data,
	}: IResponse<T> & {
		id: number | string;
		data: L;
	}) =>
		utils.httpPutMethod({
			apiData: {
				url: `${url}/${id}`,
				header: utils.header(),
				data,
			},
			actionType,
			onSuccess,
			onFailure,
		});

export const deleteAPI =
	<T = undefined, L = unknown>({ actionType, url }: { actionType: utils.I_ACTION_TYPE; url: string }) =>
	({
		onFailure,
		onSuccess,
		data,
	}: IGetQuery<T> & {
		data: L;
	}) =>
		utils.httpDeleteMethod({
			apiData: {
				url,
				header: utils.header(),
				data,
			},
			actionType,
			onSuccess,
			onFailure,
		});

export const deleteByIdAPI =
	({ actionType, url }: { actionType: utils.I_ACTION_TYPE; url: string }) =>
	({ id, onFailure, onSuccess }: IResponse & { id: string }) =>
		utils.httpDeleteMethod({
			apiData: {
				url: `${url}/${id}`,
				header: utils.header(),
			},
			actionType,
			onFailure,
			onSuccess,
		});
